import React from 'react';
import { connect } from 'react-redux';
import { OverlayDrawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter, Button, Text, Field, Input, Checkbox, Spinner } from '@fluentui/react-components';
import { Dismiss24Regular, Edit12Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './PractitionerConsumptionRateDrawer.Functions';
import { CurrencySelectorComponent } from '../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import { formatCurrencyAmount } from '../PractitionerConsumptions/PractitionerConsumptionCurrency';
import { usePractitionerConsumptionRateDrawerStyles } from './PractitionerConsumptionRateDrawer.Styles';

const PractitionerConsumptionRateDrawerComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = usePractitionerConsumptionRateDrawerStyles();
  const [newRate, setNewRate] = React.useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = React.useState<string>('USD');
  const [hasCurrencyBeenChosen, setHasCurrencyBeenChosen] = React.useState(false);
  const [applyToSettlement, setApplyToSettlement] = React.useState(false);

  const currentRate = props.Consumption?.rate;
  const hasRelatedSettlement = ComponentFunctions.canApplyExpandedScope(props.Consumption);

  React.useEffect(() => {
    setNewRate(currentRate !== undefined && currentRate !== null ? String(currentRate) : '');
    setSelectedCurrency(props.Consumption?.currencyCode || props.Consumption?.originalCurrencyCode || 'USD');
    setHasCurrencyBeenChosen(false);
    setApplyToSettlement(false);
  }, [props.Consumption?.id, currentRate, props.isOpen]);

  const hasSameCurrency = !!selectedCurrency && selectedCurrency === props.Consumption?.originalCurrencyCode;

  React.useEffect(() => {
    if (hasSameCurrency) {
      setNewRate('1');
    }
  }, [hasSameCurrency]);

  React.useEffect(() => {
    if (props.Consumption?.rateChanged && !props.isSaving && props.isOpen) {
      props.onClose();
    }
  }, [props.Consumption?.rateChanged, props.isSaving, props.isOpen, props.onClose]);

  const normalizedRate = hasSameCurrency ? 1 : ComponentFunctions.normalizeRateInput(newRate);
  const hasSelectedCurrency = !!selectedCurrency;
  const hasInvalidRate = newRate !== '' && normalizedRate === undefined && !hasSameCurrency;
  const canSave = hasSelectedCurrency && hasCurrencyBeenChosen && normalizedRate !== undefined && !props.isSaving;

  const currentUnitAmount = props.Consumption?.unitAmount;
  const currentCurrencyCode = props.Consumption?.currencyCode;
  const originalUnitAmount = props.Consumption?.originalUnitAmount;
  const sourceAmountForPreview =
    originalUnitAmount !== undefined && originalUnitAmount !== null && originalUnitAmount > 0
      ? originalUnitAmount
      : props.Consumption?.unitAmount;

  const previewAmount = sourceAmountForPreview !== undefined && normalizedRate !== undefined
    ? sourceAmountForPreview * normalizedRate
    : undefined;

  const onConfirm = () => {
    if (!props.Consumption || !canSave) return;

    props.ChangeRate(
      props.PractitionerId,
      props.ConsumptionId,
      normalizedRate!,
      applyToSettlement,
      applyToSettlement ? props.Consumption.relatedSettlementId : undefined,
      selectedCurrency,
    );
  };

  return (
    <OverlayDrawer
      open={props.isOpen}
      onOpenChange={(_, data) => { if (!data.open) props.onClose(); }}
      position='end'
      size='medium'
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance='subtle'
              aria-label='Close'
              icon={<Dismiss24Regular />}
              onClick={props.onClose}
            />
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit12Regular />
            <span>{t('consumptionRateWorkflow.drawerTitle')}</span>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.body}>

          {/* ── Zona 1: Valores actuales (solo lectura) ── */}
          <div className={styles.infoCard}>
            <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.currentValue')}</Text>
            <div className={styles.dataGrid}>
              <div className={styles.dataItem}>
                <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.currentRate')}</Text>
                <Text className={styles.dataValue}>{props.Consumption?.rate ?? '-'}</Text>
              </div>
              <div className={styles.dataItem}>
                <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.rateType')}</Text>
                <Text className={styles.dataValue}>{ComponentFunctions.getRateTypeLabel(props.Consumption?.rateType)}</Text>
              </div>
              <div className={styles.dataItem}>
                <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.currentAmountInCurrency')}</Text>
                <Text className={styles.dataValue}>{formatCurrencyAmount(currentUnitAmount, currentCurrencyCode)}</Text>
              </div>
            </div>
            {hasRelatedSettlement && (
              <Text className={styles.settlementMeta}>
                {t('consumptionRateWorkflow.relatedSettlement')}: {props.Consumption?.relatedSettlementName || props.Consumption?.relatedSettlementId}
              </Text>
            )}
          </div>

          {/* ── Zona 2: Edición — moneda primero, luego cotización ── */}
          <div className={styles.editCard}>
            <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.drawerTitle')}</Text>

            <div className={styles.fieldFull}>
              <CurrencySelectorComponent
                label={t('consumptionRateWorkflow.currencyCode')}
                isRequired={false}
                errorMessage={undefined}
                defaultSelected={selectedCurrency}
                onCurrencyChange={(currency: string) => {
                  setSelectedCurrency(currency);
                  setHasCurrencyBeenChosen(true);
                }}
              />
            </div>

            <div className={styles.fieldFull}>
              <Field
                label={t('consumptionRateWorkflow.newRate')}
                validationMessage={hasInvalidRate ? t('consumptionRateWorkflow.validation.invalidRate') : undefined}
              >
                <Input
                  style={{ width: '100%' }}
                  type='number'
                  step={0.000001}
                  value={newRate}
                  disabled={!hasCurrencyBeenChosen || hasSameCurrency}
                  onChange={(_, data) => setNewRate(data.value)}
                />
              </Field>
            </div>

            {hasSameCurrency && (
              <Text className={styles.sameCurrencyBadge}>{t('consumptionRateWorkflow.sameCurrencyFixedRate')}</Text>
            )}
          </div>

          {/* ── Zona 3: Resultado de la conversión ── */}
          <div className={styles.resultCard}>
            <Text className={styles.sectionTitle}>{t('consumptionRateWorkflow.previewAmountInSelectedCurrency')}</Text>
            <div className={styles.dataGrid}>
              <div className={styles.dataItem}>
                <Text className={styles.dataLabel}>{`${selectedCurrency || '-'}`}</Text>
                <Text className={styles.dataValueAccent}>
                  {previewAmount !== undefined ? formatCurrencyAmount(previewAmount, selectedCurrency) : '-'}
                </Text>
              </div>
              <div className={styles.dataItem}>
                <Text className={styles.dataLabel}>{t('consumptionRateWorkflow.newRateToApply')}</Text>
                <Text className={styles.dataValue}>{newRate || '-'}</Text>
              </div>
            </div>
          </div>

          {hasRelatedSettlement ? (
            <Checkbox
              label={t('consumptionRateWorkflow.applyToRelatedSettlement')}
              checked={applyToSettlement}
              onChange={(_, data) => setApplyToSettlement(!!data.checked)}
            />
          ) : (
            <Text size={200}>{t('consumptionRateWorkflow.expandedUnavailable')}</Text>
          )}

          {props.error && (
            <Text className={styles.errorBadge}>{props.error}</Text>
          )}
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div className={styles.footer}>
          <Button appearance='secondary' onClick={props.onClose} disabled={props.isSaving}>{t('common.cancel')}</Button>
          <Button className={styles.confirmButton} appearance='primary' onClick={onConfirm} disabled={!canSave}>
            {props.isSaving && <Spinner size='extra-tiny' />}
            {t('consumptionRateWorkflow.confirm')}
          </Button>
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps,
)(PractitionerConsumptionRateDrawerComponent as any);
